// ===============================================
// Cleaning Calculator - Helper Functions & Constants
// ===============================================
// This file contains all utility functions, constants, and calculation helpers
// extracted from CleaningCalculator.js for better organization and reusability

// ----------------- Utility Functions -----------------

/**
 * Round a number to specified decimal places
 * @param {number} n - Number to round
 * @param {number} d - Decimal places (default: 2)
 * @returns {number} Rounded number
 */
export const round = (n, d = 2) => Math.round(n * 10 ** d) / 10 ** d;

/**
 * Clamp a value between min and max
 * @param {number} v - Value to clamp
 * @param {number} lo - Minimum value
 * @param {number} hi - Maximum value
 * @returns {number} Clamped value
 */
export const clamp = (v, lo, hi) => Math.max(lo, Math.min(hi, v));

/**
 * Format number as currency
 * @param {number} n - Number to format
 * @param {string} currency - Currency code (USD, CAD, etc.)
 * @returns {string} Formatted currency string
 */
export const fmt = (n, currency) => {
  if (!isFinite(n)) return "-";
  try {
    return new Intl.NumberFormat(undefined, { style: "currency", currency, maximumFractionDigits: 0 }).format(n);
  } catch {
    return `${currency} ${Math.round(n).toLocaleString()}`;
  }
};

/**
 * Format number with no decimals
 * @param {number} n - Number to format
 * @returns {string} Formatted string
 */
export const fmt0 = (n) => (isFinite(n) ? Math.round(n).toLocaleString() : "-");

/**
 * Format number as currency with specific decimal places
 * @param {number} n - Number to format
 * @param {string} currency - Currency code
 * @param {number} digits - Decimal places (default: 2)
 * @returns {string} Formatted currency string
 */
export const fmtRate = (n, currency, digits = 2) => {
  if (!isFinite(n)) return "-";
  try {
    return new Intl.NumberFormat(undefined, { style: "currency", currency, minimumFractionDigits: digits, maximumFractionDigits: digits }).format(n);
  } catch {
    return `${currency} ${Number(n).toFixed(digits)}`;
  }
};

/**
 * Calculate travel total cost (first 2 visits are free)
 * @param {number} visits - Total number of visits
 * @param {number} costPer - Cost per visit
 * @returns {number} Total travel cost
 */
export const travelTotal = (visits, costPer) => Math.max(0, (parseFloat(visits) || 0) - 2) * Math.max(0, parseFloat(costPer) || 0);

/**
 * Check if number is non-zero (greater than threshold)
 * @param {number} v - Value to check
 * @returns {boolean} True if non-zero
 */
export const nz = (v) => Math.abs(Number(v) || 0) > 1e-3;

// ----------------- Location Constants -----------------

export const COUNTRY_OPTIONS = ["United States", "Canada"];

export const STATE_PROVINCE_OPTIONS = {
  "United States": [
    "Alabama","Alaska","Arizona","Arkansas","California","Colorado","Connecticut","Delaware","District of Columbia","Florida","Georgia","Hawaii","Idaho","Illinois","Indiana","Iowa","Kansas","Kentucky","Louisiana","Maine","Maryland","Massachusetts","Michigan","Minnesota","Mississippi","Missouri","Montana","Nebraska","Nevada","New Hampshire","New Jersey","New Mexico","New York","North Carolina","North Dakota","Ohio","Oklahoma","Oregon","Pennsylvania","Rhode Island","South Carolina","South Dakota","Tennessee","Texas","Utah","Vermont","Virginia","Washington","West Virginia","Wisconsin","Wyoming"
  ],
  "Canada": [
    "Alberta","British Columbia","Manitoba","New Brunswick","Newfoundland and Labrador","Nova Scotia","Ontario","Prince Edward Island","Quebec","Saskatchewan"
  ]
};

export const LOCATION_FACTORS = Object.freeze({
  "Alabama": 0.89,"Alaska": 1.10,"Arizona": 1.03,"Arkansas": 0.88,"California": 1.21,"Colorado": 1.08,"Connecticut": 1.09,"Delaware": 1.02,"District of Columbia": 1.13,"Florida": 1.02,"Georgia": 0.97,"Hawaii": 1.30,"Idaho": 1.00,"Illinois": 1.03,"Indiana": 0.94,"Iowa": 0.93,"Kansas": 0.92,"Kentucky": 0.90,"Louisiana": 0.91,"Maine": 1.00,"Maryland": 1.06,"Massachusetts": 1.11,"Michigan": 1.00,"Minnesota": 1.03,"Mississippi": 0.84,"Missouri": 0.94,"Montana": 1.00,"Nebraska": 0.93,"Nevada": 1.05,"New Hampshire": 1.03,"New Jersey": 1.10,"New Mexico": 0.98,"New York": 1.18,"North Carolina": 1.00,"North Dakota": 0.95,"Ohio": 0.95,"Oklahoma": 0.90,"Oregon": 1.07,"Pennsylvania": 1.03,"Rhode Island": 1.03,"South Carolina": 0.97,"South Dakota": 0.93,"Tennessee": 0.96,"Texas": 1.00,"Utah": 1.03,"Vermont": 1.02,"Virginia": 1.04,"Washington": 1.09,"West Virginia": 0.85,"Wisconsin": 0.98,"Wyoming": 0.95,
  "Alberta": 1.00,"British Columbia": 1.13,"Manitoba": 0.91,"New Brunswick": 0.91,"Newfoundland and Labrador": 0.91,"Nova Scotia": 0.92,"Ontario": 1.09,"Prince Edward Island": 0.91,"Quebec": 0.95,"Saskatchewan": 0.92,
  "__default": 1.0
});

// ----------------- Auto Rate Functions -----------------

/**
 * Get automatic labor rate based on location
 * @param {string} country - Country name
 * @param {string} region - State/Province name
 * @returns {number} Labor rate per hour
 */
export function getAutoLaborRate(country, region) {
  const base = country === 'United States' ? 40 : 38;
  const loc = LOCATION_FACTORS[region] ?? 1.0;
  return Math.round(clamp(base * loc, 28, 95));
}

/**
 * Get automatic Time & Material rate based on location
 * @param {string} country - Country name
 * @param {string} region - State/Province name
 * @returns {number} T&M rate per hour
 */
export function getAutoTMRate(country, region) {
  const base = country === 'United States' ? 65 : 62;
  const loc = LOCATION_FACTORS[region] ?? 1.0;
  return Math.round(clamp(base * loc, 35, 120));
}

/**
 * Get automatic lift daily cost by location (scaled)
 * @param {string} region - State/Province name
 * @returns {number} Daily lift cost
 */
export function getAutoLiftDaily(region) {
  const base = 300;
  const loc = LOCATION_FACTORS[region] ?? 1.0;
  return Math.round((base * loc) / 5) * 5;
}

/**
 * Calculate default lift days from number of visits
 * @param {number} visits - Number of site visits
 * @returns {number} Number of lift days
 */
export function defaultLiftDaysFromVisits(visits) {
  const v = Math.max(0, parseInt(visits || 0, 10));
  return Math.max(1, Math.ceil(v / 2));
}

// ----------------- Project Type Constants -----------------

export const PROJECT_TYPES = [
  "Airport","Athletic","Auto Office-Bay","Bank","Car dealership","Carpet clean","Corporate","Education","Fitness","Flooring service","Gas Station","Grocery","Healthcare","Warehouse","Self Storage","Distribution Center","Industrial","Interior tenant fit out","Job trailer","Library","Military Installation","Miscellaneous","Mixed use","Apartment Complex","Student Housing","Hotel","Police-Fire Station","Recreation Center","Residential Home","Restaurant","Retail","Senior living Retirement","Theatre","Worship","Zoo"
];

export const TYPE_PRODUCTIVITY = {
  Warehouse: { rough: 1.70, final: 1.65, fluff: 1.55 },
  "Distribution Center": { rough: 1.60, final: 1.55, fluff: 1.45 },
  "Self Storage": { rough: 1.50, final: 1.45, fluff: 1.35 },
  Industrial: { rough: 1.68, final: 1.62, fluff: 1.52 },
  Healthcare: { rough: 0.80, final: 0.70, fluff: 0.85 },
  default: { rough: 1.0, final: 1.0, fluff: 1.0 }
};

export const TYPE_RATE_MULT = {
  Healthcare: 1.35, Grocery: 1.20, Retail: 1.10, Corporate: 1.08, Education: 1.08,
  "Apartment Complex": 1.10, "Student Housing": 1.15, "Hotel": 1.25, "Senior living Retirement": 1.18, "Police-Fire Station": 1.12, "Mixed use": 1.12,
  Warehouse: 0.55, "Distribution Center": 0.58, "Self Storage": 0.60, Industrial: 0.55, "Residential Home": 1.05,
  default: 1.0,
};

/**
 * Get productivity multiplier based on building size
 * @param {number} areaM2 - Area in square meters
 * @returns {number} Productivity multiplier
 */
export function sizeProductivityMultiplier(areaM2) {
  if (areaM2 <= 500) return 0.85;
  if (areaM2 <= 2000) return 0.95;
  if (areaM2 <= 5000) return 1.0;
  if (areaM2 <= 15000) return 1.08;
  if (areaM2 <= 40000) return 1.18;
  return 1.28;
}

// ----------------- Pricing Constants -----------------

export const LEVEL_PENALTY_PER_LEVEL = 0.03;
export const PHASE_PASS_PREMIUM = 0.30;
export const NATIONAL_BASE_SQFT = 0.26;

// ----------------- Janitorial Constants -----------------

export const JANITORIAL_BASE_M2_HR = 350;

export const JANITORIAL_TYPE_MULT = {
  Healthcare: 0.80,
  Warehouse: 1.15,
  "Distribution Center": 1.12,
  "Self Storage": 1.10,
  Corporate: 1.05,
  default: 1.0,
};

// ----------------- Pressure Wash Constants -----------------

export const PRESSURE_PRESETS = Object.freeze({
  manual: { label: 'Off / Manual' },
  around: { label: 'Around building only', width: 6, entrances: 0, pad_size: 0 },
  sidewalks_pads: { label: 'Sidewalks + entrances + pads', width: 6, entrances: 3, pad_size: 60 },
});

/**
 * Estimate building perimeter from area
 * @param {number} areaFt2 - Area in square feet
 * @returns {number} Estimated perimeter in feet
 */
export function estimatePerimeterFromAreaFt2(areaFt2){
  return 4 * Math.sqrt(Math.max(0, areaFt2 || 0));
}

/**
 * Compute pressure wash area from preset
 * @param {number} areaFt2 - Building area in square feet
 * @param {string} presetKey - Preset key (manual, around, sidewalks_pads)
 * @returns {number} Pressure wash area
 */
export function computePressureAreaFromPreset(areaFt2, presetKey){
  const p = PRESSURE_PRESETS[presetKey];
  if (!p || presetKey === 'manual') return 0;
  const perim = estimatePerimeterFromAreaFt2(areaFt2);
  const width = Math.max(0, p.width || 0);
  const pads = Math.max(0, p.entrances || 0) * Math.max(0, p.pad_size || 0);
  return Math.round(perim * width + pads);
}

// ----------------- Phase Helper Functions -----------------

/**
 * Compute automatic site visits based on labor hours and phases
 * @param {number} laborHours - Total labor hours
 * @param {number} crewSize - Crew size
 * @param {object} act - Active phases {rough, final, fluff}
 * @param {object} passes - Passes per phase {rough, final, fluff}
 * @returns {number} Number of site visits
 */
export function computeAutoVisits(laborHours, crewSize, act, passes) {
  const roughPass = act?.rough ? Math.max(1, parseInt(passes?.rough ?? 1, 10)) : 0;
  const finalPass = act?.final ? Math.max(1, parseInt(passes?.final ?? 1, 10)) : 0;
  const fluffPass = act?.fluff ? Math.max(1, parseInt(passes?.fluff ?? 1, 10)) : 0;
  const sumPasses = roughPass + finalPass + fluffPass;
  const crewDays = Math.max(0, Number(laborHours) || 0) / (Math.max(1, Number(crewSize) || 1) * 8);
  const extraTrips = Math.max(0, Math.ceil(crewDays) - 1);
  return Math.max(1, sumPasses + extraTrips);
}

/**
 * Compute phase preset configuration
 * @param {number} count - Number of phases (1, 2, or 3)
 * @param {string} singleChoice - Single phase choice (rough, final, or fluff)
 * @param {string} twoCombo - Two phase combination (rough_final, final_fluff, rough_fluff)
 * @returns {object} Phase configuration {act, w}
 */
export function computePhasePreset(count, singleChoice, twoCombo = 'rough_final') {
  if (count === 1) {
    const act = { rough: false, final: false, fluff: false };
    const w = { rough: 0, final: 0, fluff: 0 };
    const key = singleChoice === 'rough' ? 'rough' : singleChoice === 'fluff' ? 'fluff' : 'final';
    act[key] = true; w[key] = 100;
    return { act, w };
  }
  if (count === 2) {
    switch (twoCombo) {
      case 'final_fluff':
        return { act: { rough: false, final: true,  fluff: true  }, w: { rough: 0,  final: 70, fluff: 30 } };
      case 'rough_fluff':
        return { act: { rough: true,  final: false, fluff: true  }, w: { rough: 60, final: 0,  fluff: 40 } };
      case 'rough_final':
      default:
        return { act: { rough: true,  final: true,  fluff: false }, w: { rough: 35, final: 65, fluff: 0  } };
    }
  }
  return { act: { rough: true, final: true, fluff: true }, w: { rough: 30, final: 50, fluff: 20 } };
}

/**
 * Compute bid calculation weights (different from display weights, can exceed 100%)
 * @param {number} count - Number of phases
 * @param {string} singleChoice - Single phase choice
 * @param {string} twoCombo - Two phase combination
 * @returns {object} Bid weights {rough, final, fluff}
 */
export function computeBidCalculationWeights(count, singleChoice, twoCombo = 'rough_final') {
  if (count === 1) {
    const w = { rough: 0, final: 0, fluff: 0 };
    const key = singleChoice === 'rough' ? 'rough' : singleChoice === 'fluff' ? 'fluff' : 'final';
    w[key] = 100;
    return w;
  }
  if (count === 2) {
    switch (twoCombo) {
      case 'final_fluff':
        return { rough: 0, final: 85, fluff: 40 };
      case 'rough_fluff':
        return { rough: 60, final: 0, fluff: 40 };
      case 'rough_final':
      default:
        return { rough: 45, final: 95, fluff: 0 };
    }
  }
  return { rough: 45, final: 85, fluff: 30 };
}

/**
 * Compute phase effective rate multiplier for pricing
 * @param {number} count - Number of phases
 * @param {string} singleChoice - Single phase choice
 * @param {string} twoCombo - Two phase combination
 * @returns {number} Rate multiplier
 */
export function computePhaseEffectiveRateMultiplier(count, singleChoice, twoCombo = 'rough_final') {
  if (count === 1) {
    return 1.0;
  }
  if (count === 2) {
    switch (twoCombo) {
      case 'final_fluff':
        return 1.25;
      case 'rough_fluff':
        return 1.0;
      case 'rough_final':
      default:
        return 1.40;
    }
  }
  return 1.60;
}

/**
 * Compute phase factor from active phases
 * @param {object} act - Active phases {rough, final, fluff}
 * @returns {number} Phase factor
 */
export function phaseFactorFromAct(act){
  const c = Number(act.rough) + Number(act.final) + Number(act.fluff);
  return 1 + Math.max(0, c - 1) * PHASE_PASS_PREMIUM;
}

// ----------------- Units/Baths Helper Functions -----------------

/**
 * Derive simple (per-unit / per-bath) defaults from Units Mix rows
 * @param {Array} rows - Units mix rows
 * @param {boolean} useUnscaled - Use unscaled rates (default: true)
 * @returns {object} {perUnit, perBath}
 */
export function deriveSimpleFromMix(rows, useUnscaled = true) {
  if (!Array.isArray(rows) || rows.length === 0) return { perUnit: 0, perBath: 0 };
  const studio = rows.find(r => (r.type || "").toLowerCase().includes("studio"));
  const pickBase = (r) => useUnscaled ? (r?.baseRateUnscaled ?? r?.baseRate ?? 0) : (r?.baseRate ?? r?.baseRateUnscaled ?? 0);
  const pickBath = (r) => useUnscaled ? (r?.ratePerBathUnscaled ?? r?.ratePerBath ?? 0) : (r?.ratePerBath ?? r?.ratePerBathUnscaled ?? 0);
  if (studio) {
    return { perUnit: Math.round(Number(pickBase(studio)) || 0), perBath: Math.round(Number(pickBath(studio)) || 0) };
  }
  const bases = rows.map(pickBase).filter(x => isFinite(x) && x > 0);
  const baths = rows.map(pickBath).filter(x => isFinite(x) && x > 0);
  const avgBase = bases.length ? bases.reduce((a,b)=>a+b,0)/bases.length : 0;
  const avgBath = baths.length ? baths.reduce((a,b)=>a+b,0)/baths.length : 0;
  return { perUnit: Math.round(avgBase), perBath: Math.round(avgBath) };
}

